/**
 * ELIO Board USB Serial 연결 관리 (Web Serial API).
 * CH340/CH341 USB-Serial, baudRate 115200.
 * 분석: elio-board-firmware/USBDongle/docs/PROTOCOL_ANALYSIS.md §3
 */

const {encodePacket, ElioParser, CMD_STATUS, CMD_ENV} = require('./ElioPacket');

const BAUD_RATE = 115200;
const INIT_PACKET = new Uint8Array([0x20, 0x50, 0x00, 0x00, 0x00]);

class ElioSerial {
    constructor () {
        this._port = null;
        this._reader = null;
        this._writer = null;
        this._running = false;
        this.connected = false;

        this.status = {
            DC1: 0,
            DC2: 0,
            SV1: 0,
            SV2: 0,
            V3: 0,
            V5: 0,
            IO1: 0,
            IO2: 0,
            IO3: 0,
            IO4: 0,
            SONIC: 0,
            LINE1: 0,
            LINE2: 0
        };
        this.env = {
            TEMPERATURE: 0,
            HUMIDITY: 0,
            WATER: 0
        };

        this._parser = new ElioParser(p => this._handle(p));
        this.onConnect = null;
        this.onDisconnect = null;
    }

    async connect () {
        if (!navigator.serial) {
            throw new Error('Web Serial API 미지원 브라우저');
        }
        this._port = await navigator.serial.requestPort();
        await this._port.open({baudRate: BAUD_RATE});
        this._writer = this._port.writable.getWriter();
        this.connected = true;
        await this._sendRaw(encodePacket(INIT_PACKET));
        this._readLoop();
        if (this.onConnect) this.onConnect();
    }

    async disconnect () {
        this._running = false;
        if (this._reader) {
            try {
                await this._reader.cancel();
            } catch (e) {
                // ignore — 이미 닫힘
            }
            this._reader = null;
        }
        if (this._writer) {
            try {
                this._writer.releaseLock();
            } catch (e) {
                // ignore
            }
            this._writer = null;
        }
        if (this._port) {
            try {
                await this._port.close();
            } catch (e) {
                // ignore
            }
            this._port = null;
        }
        this.connected = false;
        if (this.onDisconnect) this.onDisconnect();
    }

    async send (data) {
        await this._sendRaw(encodePacket(data));
    }

    async _sendRaw (bytes) {
        if (this._writer) {
            await this._writer.write(bytes);
        }
    }

    async _readLoop () {
        this._running = true;
        this._reader = this._port.readable.getReader();
        try {
            while (this._running) {
                const {value, done} = await this._reader.read();
                if (done) break;
                if (value) this._parser.addData(value);
            }
        } catch (e) {
            if (this._running) await this.disconnect();
        } finally {
            if (this._reader) {
                this._reader.releaseLock();
                this._reader = null;
            }
        }
    }

    /**
     * cmd_status / cmd_env 페이로드 디코딩.
     * 분석: PROTOCOL_ANALYSIS.md §3.4 (app_usb_status_t 16B + header 2B = 18B)
     * @param {Uint8Array} payload (헤더 포함)
     */
    _handle (payload) {
        const cmd = payload[1];
        if (cmd === CMD_STATUS && payload.length >= 18) {
            this.status.DC1 = payload[2];
            this.status.DC2 = payload[3];
            this.status.SV1 = payload[4];
            this.status.SV2 = payload[5];
            this.status.V3 = payload[6];
            this.status.V5 = payload[7];
            this.status.IO1 = payload[8];
            this.status.IO2 = payload[9];
            this.status.IO3 = payload[10];
            this.status.IO4 = payload[11];
            this.status.SONIC = (payload[13] << 8) | payload[12];
            // 펌웨어 line1/line2 raw 16-bit. 토이 약속: 감지=0, 미감지=nonzero.
            // logical: 감지→1, 미감지→0
            this.status.LINE1 = (payload[14] === 0 && payload[15] === 0) ? 1 : 0;
            this.status.LINE2 = (payload[16] === 0 && payload[17] === 0) ? 1 : 0;
        } else if (cmd === CMD_ENV && payload.length >= 8) {
            this.env.TEMPERATURE = (payload[3] << 8) | payload[2];
            this.env.HUMIDITY = (payload[5] << 8) | payload[4];
            this.env.WATER = (payload[7] << 8) | payload[6];
        }
    }
}

module.exports = ElioSerial;
